import type { QueryClient } from '@tanstack/react-query';

import { onBeforeSignOut } from '@/shared/auth';
import { listenToWorker } from '@/shared/pwa/register';

import { notificationKeys } from '../api';
import { navigateTo } from '../prefs';

import { fetchPushConfig, pushKeys } from './api';
import { forgetDevicePush, syncDevicePush } from './device';

/**
 * What the service worker tells the page: a notification was clicked (open its link inside the running app — the worker
 * waits for the page's answer, then focuses the window), a push arrived (the lists are stale), the browser replaced the push
 * subscription (register the new one). Also: signing out switches push off for this device, so that the next person on a
 * shared computer does not receive the previous user's notifications. Returns the function that stops listening.
 */
export function registerPushMessages({ queryClient }: { queryClient: QueryClient }): () => void {
  const stopSignOut = onBeforeSignOut(forgetDevicePush);
  const stopWorker = listenToWorker({
    navigate: navigateTo,
    push: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    subscriptionChanged: () => {
      queryClient
        .query({ queryKey: pushKeys.config, queryFn: ({ signal }) => fetchPushConfig(signal) })
        .then(syncDevicePush)
        .then(() => queryClient.invalidateQueries({ queryKey: pushKeys.devices }))
        .catch(() => undefined);
    },
  });
  return () => {
    stopSignOut();
    stopWorker();
  };
}
